import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../context/AppContext.tsx';
import {
  ShieldCheck,
  Compass,
  HeartHandshake,
  Award,
  Star,
  Clock,
  Share2,
  Copy,
  ArrowLeft,
} from 'lucide-react';
import { StaticPageValueItem } from '../../types.ts';

export default function AboutPage() {
  const { settings, copyPageUrl, navigate } = useApp();

  const about = settings?.staticPages?.aboutUs;
  const primaryColor = settings?.theme.primaryColor || '#059669';

  React.useEffect(() => {
    const customTab = settings?.pageTabTitles?.about?.trim();
    if (customTab) {
      document.title = customTab;
    } else {
      const pageTitle = about?.metaTitle || about?.title || 'Tentang Kami';
      const suffix = settings?.pageTabTitles?.titleSuffix !== undefined ? settings.pageTabTitles.titleSuffix : ` | ${settings?.siteName || ''}`;
      document.title = `${pageTitle}${suffix}`;
    }
  }, [about?.title, about?.metaTitle, settings?.siteName, settings?.pageTabTitles?.about, settings?.pageTabTitles?.titleSuffix]);

  const renderIcon = (iconName?: string) => {
    switch (iconName) {
      case 'Compass':
        return <Compass className="w-6 h-6" />;
      case 'HeartHandshake':
        return <HeartHandshake className="w-6 h-6" />;
      case 'Award':
        return <Award className="w-6 h-6" />;
      case 'ShieldCheck':
        return <ShieldCheck className="w-6 h-6" />;
      case 'Clock':
        return <Clock className="w-6 h-6" />;
      case 'Star':
      default:
        return <Star className="w-6 h-6" />;
    }
  };

  const defaultValues: StaticPageValueItem[] = [
    {
      id: 'val-1',
      icon: 'Compass',
      title: 'Eksplorasi Tak Terbatas',
      description: 'Menghadirkan rute wisata dan petualangan berkesan dengan standar keamanan tinggi.',
    },
    {
      id: 'val-2',
      icon: 'HeartHandshake',
      title: 'Pemberdayaan UMKM',
      description: 'Membuka akses pasar yang luas bagi produk unggulan petani dan pengrajin lokal.',
    },
    {
      id: 'val-3',
      icon: 'Award',
      title: 'Kualitas Profesional',
      description: 'Didukung tim bersertifikasi dan arsitektur digital berkecepatan tinggi.',
    },
  ];

  const valuesList = about?.values && about.values.length > 0 ? about.values : defaultValues;

  return (
    <div id="about-page-view" className="py-10 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Navigation & Action Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <button
            onClick={() => navigate('home')}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke Beranda</span>
          </button>

          <button
            onClick={() => copyPageUrl('/about', 'Tentang Kami')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all shadow-2xs border border-slate-200"
            title="Salin Tautan Halaman Ini"
          >
            <Copy className="w-3.5 h-3.5 text-slate-500" />
            <span>Salin URL (/about)</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold mb-3 border border-emerald-200">
            <Award className="w-3.5 h-3.5" />
            <span>{about?.badge || 'Profil Perusahaan & Nilai Inti'}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
            {about?.title || 'Tentang Kami'}
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-3 leading-relaxed">
            {about?.subtitle ||
              'Membangun sinergi pengalaman terbaik antara alam, kreativitas digital, dan kemajuan UMKM'}
          </p>
        </div>

        {/* Cover Image */}
        {about?.coverImage && (
          <div className="rounded-3xl overflow-hidden shadow-xl aspect-16/9 mb-12 bg-slate-900 border border-slate-200">
            <img
              src={about.coverImage}
              alt={about?.title || 'Tentang Kami'}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content Body */}
        <div className="bg-white p-6 sm:p-12 rounded-3xl border border-slate-200 shadow-xs mb-12">
          <div className="article-prose">
            <ReactMarkdown>
              {about?.content ||
                `### Selamat Datang di Platform Kami\n\nKami adalah entitas multifungsi yang berdedikasi menghadirkan solusi terbaik.`}
            </ReactMarkdown>
          </div>
        </div>

        {/* Values Grid */}
        {valuesList.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {valuesList.map((val) => (
              <div
                key={val.id}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs transition-transform hover:-translate-y-1"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-xs"
                  style={{ backgroundColor: primaryColor }}
                >
                  {renderIcon(val.icon)}
                </div>
                <h3 className="font-bold text-sm text-slate-900 mb-1.5">{val.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
